import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ModalCompAdvance } from "../../../../components/Ui/ModalComp";
import FallbackImage from "../../../../components/Common/ImageWrapper";
import { formatMoney, dateFromString } from "../../../../utils";
import { convertToTitleCase } from "../../../../utils/helpers";
import LoanDetailsModal from "./LoanDetailsModal";
import ContributionDetailsModal from "./ContributionDetailsModal";

const statusStyles = {
  PENDING: {
    background: "#FFF4E6",
    color: "#FFA500",
  },
  APPROVED: {
    background: "#E9F9EE",
    color: "#19BC31",
  },
  DISBURSED: {
    background: "#E3F2FD",
    color: "var(--color-secondary)",
  },
  DECLINED: {
    background: "#FBEAEA",
    color: "#FF4343",
  },
  PAID: {
    background: "#E9F9EE",
    color: "#19BC31",
  },
  OVERDUE: {
    background: "#FBEAEA",
    color: "#FF4343",
  },
  Repayment: {
    background: "#E9F9EE",
    color: "#3BB77E",
  },
  Disbursed: {
    background: "#FBEAEA",
    color: "#F25767",
  },
};

export default function RecentActivities({ data = [], emptyMessage, type }) {
  const navigate = useNavigate();
  const [modalToggle, setModalToggle] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState(null);
  const [selectedContribution, setSelectedContribution] = useState(null);

  const handleItemClick = (item) => {
    if (type === "loan" && item?.id) {
      // Show modal instead of navigating
      setSelectedLoanId(item.id);
      setModalToggle(true);
    } else if (type === "contribution" && item?.id) {
      // Show modal for contribution details
      setSelectedContribution(item);
      setModalToggle(true);
    }
  };

  const handleSeeMore = () => {
    if (type === "loan") {
      navigate("/super_admin/loan_management/loan_status");
    } else if (type === "contribution") {
      navigate("/super_admin/contribution/contribution_history");
    }
  };
  return (
    <div
      className="recent-activities-list"
      style={{
        background: "#fff",
        borderRadius: 0,
        boxShadow: "none",
        overflow: "hidden",
        maxWidth: "100%",
        margin: 0,
        padding: 0,
      }}
    >
      {data.length === 0 ? (
        <div className="text-center py-3 text-muted h60">{emptyMessage}</div>
      ) : (
        <>
          {/* Table Header */}
          <div
            className="d-flex align-items-center justify-content-between"
            style={{
              borderBottom: "1px solid #E0E0E0",
              padding: "12px 0",
              fontSize: 12,
              fontWeight: 600,
              color: "#666",
              background: "#F8F9FA",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <div className="d-flex align-items-center" style={{ flex: 1 }}>
              <div
                style={{
                  flex: "0 0 35%",
                  minWidth: 0,
                  paddingLeft: "4px",
                }}
              >
                Member
              </div>
              <div
                style={{
                  flex: "0 0 25%",
                  textAlign: "center",
                }}
              >
                Date
              </div>
              <div
                style={{
                  flex: "0 0 20%",
                  textAlign: "center",
                }}
              >
                Status
              </div>
              <div
                style={{
                  flex: "0 0 20%",
                  textAlign: "right",
                  paddingRight: "4px",
                }}
              >
                Amount
              </div>
            </div>
            {data.length > 0 && (
              <button
                onClick={handleSeeMore}
                className="btn btn-sm text-primary1 ms-3"
                style={{
                  borderColor: "var(--color-primary)",
                  backgroundColor: "transparent",
                  whiteSpace: "nowrap",
                  fontSize: "11px",
                  padding: "4px 12px",
                }}
              >
                See More
              </button>
            )}
          </div>
          {data.map((item, idx) => {
            const status = item?.status || "";
            const statusKey = status?.toUpperCase();
            const statusStyle = statusStyles[statusKey] || statusStyles[status] || {
              background: "#F0F0F0",
              color: "#A1A0A0",
            };
            
            // Handle date - support both createdAt and created_at
            const dateValue = item?.createdAt || item?.created_at;
            
            // Handle amount - support both amount and loan_amount fields
            const amountValue = item?.amount ?? item?.loan_amount ?? item?.totalAmount ?? 0;
            
            return (
              <div
                key={idx}
                className="d-flex align-items-center activity-row"
                onClick={() => handleItemClick(item)}
                style={{
                  borderBottom:
                    idx !== data.length - 1 ? "1px solid #F0F0F0" : "none",
                  padding: "8px 0",
                  fontSize: 12,
                  transition: "background 0.2s",
                  background: "#fff",
                  cursor: "pointer",
                  minHeight: "40px",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#F8F9FA")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "#fff")
                }
              >
                {/* Member Column */}
                <div
                  style={{
                    flex: "0 0 35%",
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FallbackImage
                    src={item?.memberImage || item?.member?.photo || "/Staff/avatar.svg"}
                    width="20px"
                    height="20px"
                    className="rounded-circle"
                    style={{ flexShrink: 0 }}
                  />
                  <span
                    className="text-dark ms-2"
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {item?.member || (item?.member?.first_name && item?.member?.last_name 
                      ? `${item.member.first_name} ${item.member.last_name}`
                      : "N/A")}
                  </span>
                </div>

                {/* Date Column */}
                <div
                  style={{
                    flex: "0 0 25%",
                    textAlign: "center",
                    fontSize: 12,
                    color: "#666",
                  }}
                >
                  {dateValue ? dateFromString(dateValue) : "-"}
                </div>

                {/* Status Column */}
                <div style={{ flex: "0 0 20%", textAlign: "center" }}>
                  <span
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: 8,
                      fontWeight: 500,
                      fontSize: 10,
                      background: statusStyle.background,
                      color: statusStyle.color,
                      minWidth: "60px",
                    }}
                  >
                    {convertToTitleCase(status)}
                  </span>
                </div>

                {/* Amount Column */}
                <div
                  style={{ flex: "0 0 20%", textAlign: "right", fontSize: 12, paddingRight: "4px" }}
                >
                  <span style={{ fontWeight: 500 }}>
                    {formatMoney(amountValue, false, "")}
                  </span>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* Loan Details Modal */}
      {type === "loan" && (
        <ModalCompAdvance
          show={modalToggle}
          handleClose={() => {
            setModalToggle(false);
            setSelectedLoanId(null);
          }}
          size="lg"
          centered={true}
          close={true}
          arrayComp={[
            {
              name: "Loan Details",
              component: (
                <LoanDetailsModal
                  loanId={selectedLoanId}
                  handleClose={() => {
                    setModalToggle(false);
                    setSelectedLoanId(null);
                  }}
                />
              ),
            },
          ]}
          modalBody={0}
        />
      )}

      {/* Contribution Details Modal */}
      {type === "contribution" && (
        <ModalCompAdvance
          show={modalToggle}
          handleClose={() => {
            setModalToggle(false);
            setSelectedContribution(null);
          }}
          size="lg"
          centered={true}
          close={true}
          arrayComp={[
            {
              name: "Contribution Details",
              component: (
                <ContributionDetailsModal
                  contribution={selectedContribution}
                  handleClose={() => {
                    setModalToggle(false);
                    setSelectedContribution(null);
                  }}
                />
              ),
            },
          ]}
          modalBody={0}
        />
      )}
    </div>
  );
}
